/* eslint-disable */
/**
 * Translation dictionary for the NeuroSophia platform.
 *
 * Convention:
 *  - Keys are namespaced "ui.area.key"
 *  - Default fallback is English. Missing keys fall back to the key itself.
 *  - Source Dutch product content (Manual body, Brain Type narrative
 *    reports, GOT chatbot system prompt) is NOT translated — those are
 *    intentionally Dutch-only deliverables and live in their own files.
 */

export const TRANSLATIONS = {
  en: {
    // ── Brand / global
    "brand.tagline": "The wisdom of your brain",
    "lang.switcher.label": "Language",
    "lang.nl": "Nederlands",
    "lang.en": "English",

    // ── Role labels
    "role.super_admin": "Super Admin",
    "role.institute_admin": "Institute Admin",
    "role.coach": "Coach",
    "role.user": "Client",

    // ── Sidebar
    "nav.institutes": "Institutes",
    "nav.coaches": "Coaches",
    "nav.clients": "Clients",
    "nav.dashboard": "Dashboard",
    "nav.quality_of_life": "Quality of Life",
    "nav.brain_type": "Brain Type",
    "nav.manual": "Manual",
    "nav.got_practice": "GOT Practice",
    "nav.upcoming_events": "Upcoming Events",
    "nav.institute_settings": "Institute Settings",
    "nav.my_account": "My Account",
    "nav.sign_out": "Sign out",
    "nav.open": "Open navigation",
    "nav.close": "Close navigation",

    // ── Landing / login
    "landing.headline_a": "Train your brain.",
    "landing.headline_b": "For free.",
    "landing.lead":
      "Create your free NeuroSophia account and unlock the same tools coaches use to help clients feel better, think clearer, and live with more grip on their emotions.",
    "landing.benefit.qol.title": "Quality of Life test",
    "landing.benefit.qol.blurb": "Reflect on 1000 days and discover your personal baseline.",
    "landing.benefit.brain.title": "Brain Type test",
    "landing.benefit.brain.blurb": "Find out which of the 9 brain types you are — in minutes.",
    "landing.benefit.manual.title": "Brain training manual",
    "landing.benefit.manual.blurb": "The four core techniques to take control of your thoughts.",
    "landing.benefit.ai.title": "Practice with an AI coach",
    "landing.benefit.ai.blurb": "Rehearse the Gevoelsomdraaitechniek with our AI guide.",
    "landing.benefit.free_pill": "Free",
    "landing.cta.register": "Create your free account",
    "landing.cta.sign_in": "Already a member? Sign in",
    "landing.sign_in_link": "Sign in",
    "landing.back_to_landing": "See what you get for free",
    "landing.hero_card_title": "A grounded space for emotional clarity and neuro-coaching.",

    // ── Login form
    "login.welcome_back": "Welcome back",
    "login.title_part_a": "Sign in to",
    "login.title_part_b": "your sanctuary.",
    "login.subtitle": "Continue your journey of self-discovery, awareness, and growth.",
    "login.email_label": "Email",
    "login.password_label": "Password",
    "login.email_placeholder": "you@example.com",
    "login.password_placeholder": "••••••••",
    "login.submit": "Sign in",
    "login.submitting": "Signing in...",
    "login.new_here_prompt": "New here?",

    // ── Footer
    "footer.copyright_prefix": "©",
    "footer.copyright_address": ", Binderij 7-L, 1185 ZH, Amstelveen, The Netherlands",
    "footer.copyright_org": "Neurosofie Stichting",

    // ── Register
    "register.headline_a": "Begin your",
    "register.headline_b": "journey.",
    "register.lead": "Pick the institute and coach you'll be working with, then set up your sign-in.",
    "register.step.institute": "Institute",
    "register.step.coach": "Coach",
    "register.step.details": "Details",
    "register.title": "Create your account",
    "register.step_institute": "Choose your institute",
    "register.step_coach": "Choose your coach",
    "register.step_details": "Your details",
    "register.q_institute": "Which institute does your coach or trainer belong to?",
    "register.q_coach": "Who is your coach?",
    "register.institute_search_placeholder": "Search by name or location…",
    "register.coach_search_placeholder": "Search by name, email or location…",
    "register.loading_institutes": "Loading institutes…",
    "register.loading_coaches": "Loading coaches…",
    "register.no_matches": "No matches.",
    "register.no_coaches_yet": "This institute has no coaches yet. Please contact them or pick another institute.",
    "register.coach_brain_type": "Brain type {n}",
    "register.search_placeholder": "Search by name, email, or location",
    "register.location_label": "Location",
    "register.brain_type_label": "Brain Type",
    "register.back": "Back",
    "register.continue": "Continue",
    "register.create_account": "Create account",
    "register.create_and_start": "Create account & start onboarding",
    "register.creating": "Creating account…",
    "register.already_account": "Already have an account?",
    "register.sign_in": "Sign in",
    "register.name_label": "Full Name",
    "register.email_label": "Email",
    "register.password_label": "Password (min. 6 characters)",
    "register.no_institutes": "No institutes available yet.",
    "register.no_coaches": "No coaches available at this institute yet.",
    "register.welcome_toast": "Welcome, {name}!",
    "register.error.load_institutes": "Could not load institutes",
    "register.error.load_coaches": "Could not load coaches",
    "register.error.generic": "Could not create your account.",

    // ── Account
    "account.title": "My Account",
    "account.description": "Update your sign-in email or password. Your other profile fields are managed by your administrator.",
    "account.profile": "Profile",
    "account.name": "Name",
    "account.email": "Email",
    "account.role": "Role",
    "account.change_email.title": "Change email",
    "account.change_email.subtitle": "Update the email you use to sign in. You'll need your current password.",
    "account.change_email.new": "New email",
    "account.change_email.current_password": "Current password",
    "account.change_email.submit": "Update email",
    "account.change_email.saving": "Saving…",
    "account.change_password.title": "Change password",
    "account.change_password.subtitle": "Use a password of at least 6 characters.",
    "account.change_password.current": "Current password",
    "account.change_password.new": "New password",
    "account.change_password.confirm": "Confirm new password",
    "account.change_password.submit": "Update password",
    "account.calendly.title": "Calendly embed code",
    "account.calendly.subtitle":
      "Paste the full embed snippet from Calendly (or just your booking URL). Clients who request a brain-type double-check will then book directly into your calendar. Leave empty to use the platform default.",
    "account.calendly.label": "Embed code or URL",
    "account.calendly.tip_prefix": "Tip: paste a URL that points to a specific event (e.g. ",
    "account.calendly.tip_suffix": ") so clients skip the event-picker and land on the booking page directly.",
    "account.calendly.detected": "Detected URL",
    "account.calendly.preview": "Preview popup",
    "account.calendly.save": "Save embed code",
    "account.calendly.saving": "Saving…",
    "account.calendly.clear": "Clear embed code",

    // ── Page headers (overlines)
    "overline.coach": "Coach",
    "overline.profile": "Profile",
    "overline.handleiding": "Handleiding",

    // ── Coach client list
    "coach_users.title": "My Clients",
    "coach_users.description":
      "Manage the clients you are coaching. They will be guided through the 1000-day reflection on first login.",
    "coach_users.add_client": "Add Client",
    "coach_users.banner.one": "1 client wants to do business",
    "coach_users.banner.many": "{count} clients want to do business",
    "coach_users.banner.total_suffix": " · {total} open requests in total",
    "coach_users.banner.description":
      "Look for the badges in the list below — click \"Mark handled\" once you've reached out.",
    "coach_users.banner.more": "and {n} more",
    "coach_users.col.client": "Client",
    "coach_users.col.email": "Email",
    "coach_users.col.brain": "Brain",
    "coach_users.col.type": "Type",
    "coach_users.col.onboarding": "Onboarding",
    "coach_users.col.added": "Added",
    "coach_users.loading": "Loading clients…",
    "coach_users.empty": "No clients yet. Add your first one.",
    "coach_users.onboarded": "Onboarded",
    "coach_users.onboarding_pending": "Pending",
    "coach_users.onboarding_complete": "Complete",
    "coach_users.mark_handled": "Mark handled",
    "coach_users.dialog.add_title": "Add Client",
    "coach_users.dialog.edit_title": "Edit Client",
    "coach_users.dialog.add_desc": "Provision the client's account. They will complete onboarding on first sign-in.",
    "coach_users.dialog.edit_desc": "Update the client's details. Leave password blank to keep it unchanged.",
    "coach_users.field.name": "Full Name",
    "coach_users.field.email": "Email",
    "coach_users.field.initial_password": "Initial Password",
    "coach_users.field.new_password_optional": "New Password (optional)",
    "coach_users.field.brain_type": "Brain Type (1–9, optional)",
    "coach_users.field.password_placeholder_edit": "Leave blank to keep current",
    "coach_users.delete_title": "Delete this client?",
    "coach_users.delete_description":
      "This will permanently delete \"{name}\" and their onboarding responses. This action cannot be undone.",

    // ── Common dialogs / actions
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.saving": "Saving…",
    "common.save_changes": "Save changes",
    "common.delete": "Delete",
    "common.deleting": "Deleting…",
    "common.confirm_delete": "Are you sure?",
    "common.edit": "Edit",
    "common.loading": "Loading…",
    "common.send": "Send",
    "common.sending": "Sending…",

    // ── Client types
    "client_type.prospect": "Prospect",
    "client_type.trainee": "Trainee",
    "client_type.coachee": "Coachee",

    // ── Brain Type page
    "brain_type.title": "My Brain Type",
    "brain_type.description_user":
      "Your brain type is a number from 1 to 9 assigned by your coach. It's part of how your coaching is tailored to you.",
    "brain_type.description_other":
      "Your assigned brain type is a number from 1 to 9. Pick or update the one that matches your assessment. You can change this at any time.",
    "brain_type.assigned": "Assigned to you",
    "brain_type.current": "Current",
    "brain_type.not_set": "Not set",
    "brain_type.coach_assigned": "Coach-assigned",
    "brain_type.readonly_note":
      "Only your coach or administrator can change your brain type. If it's missing or you think it should be different, reach out to your coach and they can update it for you.",
    "brain_type.select": "Select brain type",
    "brain_type.clear": "Clear selection",
    "brain_type.save": "Save brain type",
    "brain_type.preliminary_overline": "Preliminary · quick-test result",
    "brain_type.notset_overline": "Brain type · not set yet",
    "brain_type.preliminary_voorlopig": "Preliminary type",
    "brain_type.notset_heading": "Your brain type hasn't been set yet",
    "brain_type.preliminary_description":
      "This preliminary brain type comes from the short Dutch self-test you took during onboarding. That quick test is only reliable about 2 out of 3 times (~66%) because the underlying model is dynamic — your answers shift depending on whether you are stressed or relaxed. Your definitive brain type is only set after a short consultation with your coach. Once it is set, the full graphic and the detailed narrative report for your type will appear right on this page.",
    "brain_type.notset_description":
      "Your brain type will be determined together with your coach in a short consultation. Once it is set, the full graphic and the detailed narrative report for your type will appear right on this page.",
    "brain_type.request_consultation": "Request a consultation with your coach",
    "brain_type.sending_request": "Sending request…",
    "brain_type.coach_notified_pending": "Coach notified · pending",

    // ── Quality of Life
    "qol.title": "Quality of Life",
    "qol.description":
      "Daily check-in across four life areas. Toggle each subject to log how you're doing today.",
    "qol.subject.feel": "How you feel",
    "qol.subject.past": "Dealing with the past",
    "qol.subject.future": "Dealing with the future",
    "qol.subject.inner_self_talk": "Inner self-talk",
    "qol.option.good": "Good",
    "qol.option.neutral": "Neutral",
    "qol.option.bad": "Bad",
    "qol.chart.title": "Quality of Life · probability over time",
    "qol.chart.subtitle": "Per-day projection from your onboarding baseline and the toggles you set.",
    "qol.chart.loading": "Loading…",
    "qol.chart.empty": "Set your toggles below — the chart will start tracking from your account creation.",

    // ── Daily quote + check-in
    "quote.overline": "Quote of the day",
    "quote.source_label": "Source",
    "quote.loading": "Loading today's quote…",
    "checkin.title": "Quick check-in",
    "checkin.subtitle":
      "Before we show today's quote — do these four settings still match how you feel right now? Update anything that has shifted, or confirm they're still spot-on.",
    "checkin.confirm_button": "Yes, still accurate",
    "checkin.saving": "Saving…",
    "checkin.close_hint": "Adjust anything that has changed, then continue.",

    // ── Dashboard (user home)
    "dashboard.title": "Your dashboard",
    "dashboard.description":
      "A quick daily overview of your quote, your brain type, your Quality of Life status and your GOT practice.",
    "dashboard.qol.title": "Quality of Life · today",
    "dashboard.qol.updated": "Last updated",
    "dashboard.qol.unset": "Not set",
    "dashboard.qol.link": "Open Quality of Life",
    "dashboard.qol.empty":
      "You haven't set any Quality of Life toggles yet. Open the page to get started.",
    "dashboard.bt.title": "Your Brain Type",
    "dashboard.bt.definitive": "Confirmed by your coach",
    "dashboard.bt.preliminary": "Preliminary · from the quick test",
    "dashboard.bt.pending_dc": "Double-check requested · awaiting coach",
    "dashboard.bt.pending_consult": "Consultation requested · awaiting coach",
    "dashboard.bt.no_test": "You haven't taken the brain-type quick test yet.",
    "dashboard.bt.no_definitive": "Your brain type hasn't been confirmed yet.",
    "dashboard.bt.request_dc": "Request a double-check",
    "dashboard.bt.link": "Open your Brain Type",
    "dashboard.got.title": "GOT Practice",
    "dashboard.got.total_one": "session started",
    "dashboard.got.total_many": "sessions started",
    "dashboard.got.completed_one": "completed",
    "dashboard.got.completed_many": "completed",
    "dashboard.got.link": "Start a new practice",
    "dashboard.got.zero_state":
      "You haven't practised GOT yet. Try one short session with the AI coach.",

    // ── Request coaching
    "request_coaching.title": "Ready for the next step?",
    "request_coaching.idle":
      "Let your coach know you'd like to start coaching. They will be alerted and reach out to you.",
    "request_coaching.pending":
      "Your coach has been notified. They'll reach out shortly. You can send another nudge if needed.",
    "request_coaching.cta": "Request coaching",
    "request_coaching.sending": "Sending…",
    "request_coaching.send_again": "Send another reminder",
    "request_coaching.pending_status": "Pending · sent {date}",
    "request_coaching.toast.success": "Your coach has been notified. They'll reach out soon.",

    // ── Manual page chrome
    "manual.overline": "Manual",
    "manual.cta.title": "Practice this technique with AI",
    "manual.cta.body":
      "Want to put the Gevoelsomdraaitechniek to work right away? Our AI coach walks you through it step by step — just as described above. You can repeat the exercise as often as you like.",
    "manual.cta.button": "Start GOT exercise",
    "manual.coach_pitch":
      "These exercises become a lot easier — and faster — when you do them together with a Personal Breintrainer. A coach helps you really master the technique and can think along with you for your specific situation.",

    // ── GOT practice page
    "got.overline": "Coaching",
    "got.title": "GOT Practice",
    "got.description":
      "Rehearse the Gevoelsomdraaitechniek with an AI coach. Choose to play the client or the coach.",
    "got.role.client": "Play the client",
    "got.role.coach": "Play the coach",
    "got.start": "Start new session",
    "got.starting": "Starting…",
    "got.placeholder": "Type your message…",
    "got.send": "Send",
    "got.thinking": "Thinking…",
    "got.session_done_title": "Well done — start a new session?",
    "got.session_done_body": "You've completed this practice round. Begin again whenever you're ready.",
    "got.new_session": "New session",

    // ── Upcoming events
    "events.title": "Upcoming Events",
    "events.description": "Events scraped from your institute's website.",
    "events.loading": "Loading events…",
    "events.empty": "No upcoming events found.",
    "events.refresh": "Refresh",
    "events.source": "Source",

    // ── Onboarding conclusion
    "conclusion.title": "Welcome to NeuroSophia",
    "conclusion.subtitle":
      "Thanks for completing the reflection. Here is what's next.",
    "conclusion.request_coaching_button": "Request coaching",

    // ── Errors / toasts
    "errors.generic": "Something went wrong.",

    // ── Embed code card (coach + institute admin)
    "embed.coach.title": "Embed code for your website",
    "embed.coach.subtitle":
      "Place this snippet on your own website. Prospects who click it land directly on step 3 of sign-up, with you already pre-selected as their coach. Only their name, email, and password are left to fill in.",
    "embed.institute.title": "Embed code for your website",
    "embed.institute.subtitle":
      "Place this snippet on your institute's website. Prospects who click it land on step 2 of sign-up, where they can pick the right coach within your institute. The institute is pre-selected.",
    "embed.button_label": "Start free with NeuroSophia",
    "embed.tab.iframe": "iFrame (in-page)",
    "embed.tab.button": "Button (new tab)",
    "embed.tab.link": "Direct link",
    "embed.iframe.hint":
      "Drops the full sign-up flow inside a frame on your page. Best when you want visitors to stay on your site.",
    "embed.button.hint":
      "Opens NeuroSophia in a new tab when clicked — no iframe, fastest to deploy.",
    "embed.link.hint":
      "The raw URL behind the snippets above. Useful if you want to wrap it in your own newsletter button or share it on social media.",
    "embed.copy": "Copy",
    "embed.copied": "Copied!",
    "embed.preview": "Open in new tab",
    "embed.copy.toast": "Embed code copied to clipboard.",
    "embed.copy.error": "Could not copy — please copy manually.",

    // ── Onboarding (1000-day reflection)
    "onboarding.welcome": "Welcome, {name}",
    "onboarding.reflection_step": "Reflection {step} of {total} · 1000 days before coaching or training",
    "onboarding.final_step": "Final step",
    "onboarding.question_prefix": "How would you score",
    "onboarding.question.emotions.title": "your emotions and feelings",
    "onboarding.question.emotions.description": "Looking back at the 1000 days before coaching or training, how did your emotions and feelings show up?",
    "onboarding.question.past.title": "dealing with the past",
    "onboarding.question.past.description": "How did you relate to your past — memories, regrets, gratitude?",
    "onboarding.question.future.title": "dealing with the future",
    "onboarding.question.future.description": "How did you relate to what was ahead — hopes, fears, planning?",
    "onboarding.question.inner_self_talk.title": "your inner self talk",
    "onboarding.question.inner_self_talk.description": "How did your inner voice speak to you, on average, across those 1000 days?",
    "onboarding.question.distribute": "Distribute exactly",
    "onboarding.question.thousand_days": "1000 days",
    "onboarding.question.among": "across the six categories below.",
    "onboarding.category.perfect": "Perfect Days",
    "onboarding.category.perfect.hint": "Pure presence and joy",
    "onboarding.category.very_good": "Very Good Days",
    "onboarding.category.very_good.hint": "Light and connected",
    "onboarding.category.good": "Good Days",
    "onboarding.category.good.hint": "Generally positive",
    "onboarding.category.okayish": "Okayish Days",
    "onboarding.category.okayish.hint": "Neutral, getting by",
    "onboarding.category.bad": "Bad Days",
    "onboarding.category.bad.hint": "Heavy or stuck",
    "onboarding.category.very_bad": "Very Bad Days",
    "onboarding.category.very_bad.hint": "Deeply painful",
    "onboarding.days_allocated": "Days allocated",
    "onboarding.remaining": "Remaining",
    "onboarding.back": "Back",
    "onboarding.continue": "Continue",
    "onboarding.review": "Review",
    "onboarding.review.title_a": "Review your",
    "onboarding.review.title_b": "reflection.",
    "onboarding.review.body": "Take a breath. Confirm your answers below — you can step back and adjust before submitting.",
    "onboarding.submit": "Submit reflection",
    "onboarding.submitting": "Submitting…",
    "onboarding.toast.must_total": "Each question must total exactly 1000 days.",
    "onboarding.toast.thanks": "Thank you for sharing.",

    // ── Onboarding conclusion
    "conclusion.overline": "Onboarding · conclusion",
    "conclusion.headline_a": "Your starting",
    "conclusion.headline_b": "quality-of-life baseline",
    "conclusion.headline_c": "is",
    "conclusion.body":
      "We averaged your four answers (emotions, past, future, inner self-talk) using the same formula that anchors your Quality-of-Life chart. Where you land on the ABC-NLP map below helps you choose your next step.",
    "conclusion.subject.feelings": "Feelings",
    "conclusion.subject.past": "The Past",
    "conclusion.subject.future": "The Future",
    "conclusion.subject.inner_self_talk": "Inner Self Talk",
    "conclusion.band.coaching.title": "Coaching is your best next step",
    "conclusion.band.either.title": "Either coaching or training works well",
    "conclusion.band.training.title": "A training programme is your best fit",
    "conclusion.band.coaching.body":
      "With an average baseline of {avg}% (at or below 37%), one-on-one coaching gives you the focused support to lift the red zone first. Once you're stable above 37%, a training programme can take you further. Tap below to ask your coach to start coaching.",
    "conclusion.band.either.body":
      "With an average baseline of {avg}% (between 37% and 50%), you're in the orange zone — either coaching or training can move you forward. Request coaching below if you'd like personal guidance. An overview of relevant training programmes will be shown right after you finish onboarding.",
    "conclusion.band.training.body":
      "With an average baseline of {avg}% (above 50%), you're already in the green zone. A training programme — Practitioner, Master Practitioner or nascholing — is the most efficient next step. An overview of relevant training programmes will be shown right after you finish onboarding.",
    "conclusion.coach_notified_pending": "Coach notified · pending",
    "conclusion.sending": "Sending request…",
    "conclusion.request_coaching": "Request Coaching",
    "conclusion.eligible_note": "Request Coaching is only available for prospects and trainees.",
    "conclusion.continue_note": "You can always come back to your coach or visit the Upcoming Events page from the menu.",
    "conclusion.toast.notified": "Your coach has been notified. They'll reach out shortly.",

    // ── Brain Type Quick Test (onboarding)
    "bt_test.step_label_q1": "Question 1 of 2 or 3 · in Dutch",
    "bt_test.step_label_q2": "Question 2 of 2 or 3 · in Dutch",
    "bt_test.step_label_q3": "Question 3 of 3 · in Dutch",
    "bt_test.title": "Short brain-type check",
    "bt_test.previous": "Previous question",
    "bt_test.result_overline": "Preliminary result",
    "bt_test.result_title_a": "Your preliminary brain type is",
    "bt_test.disclaimer_title": "Heads up — this is a quick test",
    "bt_test.disclaimer_body":
      "This test is only reliable about 2 out of 3 times (~66%). The underlying model is dynamic — your answers shift depending on whether you are stressed or relaxed, so the same person can land on different types in different moods. A short follow-up with your coach is the best way to confirm your true brain type.",
    "bt_test.request_double_check": "Request a double-check by your coach",
    "bt_test.requesting": "Sending request…",
    "bt_test.requested": "Coach notified · finishing onboarding…",
    "bt_test.continue_without": "Continue without a double-check",
    "bt_test.saving": "Saving…",
    "bt_test.retake_last": "Retake last question",
    "bt_test.toast.requested": "Your coach has been asked for a double-check.",
    "bt_test.toast.could_not_send": "Could not send request",
    "bt_test.preview.overline": "Preview · first two paragraphs",
    "bt_test.preview.disclaimer":
      "This is just a taste of your brain-type report. The quick test is still too uncertain to show the full graphic and the rest of the report. There is much more — but to unlock it, your coach first needs to confirm your brain type with a short double-check.",
  },

  // ──────────────────────────────────────────────────────────────────
  nl: {
    // Brand / global
    "brand.tagline": "De wijsheid van je brein",
    "lang.switcher.label": "Taal",
    "lang.nl": "Nederlands",
    "lang.en": "English",

    // Role labels
    "role.super_admin": "Super Admin",
    "role.institute_admin": "Instituut Admin",
    "role.coach": "Coach",
    "role.user": "Cliënt",

    // Sidebar
    "nav.institutes": "Instituten",
    "nav.coaches": "Coaches",
    "nav.clients": "Cliënten",
    "nav.dashboard": "Dashboard",
    "nav.quality_of_life": "Kwaliteit van Leven",
    "nav.brain_type": "Breintype",
    "nav.manual": "Handleiding",
    "nav.got_practice": "GOT Oefenen",
    "nav.upcoming_events": "Aankomende Events",
    "nav.institute_settings": "Instituutinstellingen",
    "nav.my_account": "Mijn Account",
    "nav.sign_out": "Uitloggen",
    "nav.open": "Menu openen",
    "nav.close": "Menu sluiten",

    // Landing
    "landing.headline_a": "Train je brein.",
    "landing.headline_b": "Gratis.",
    "landing.lead":
      "Maak gratis een NeuroSophia-account aan en krijg toegang tot dezelfde tools waarmee coaches hun cliënten helpen zich beter te voelen, helderder te denken en meer grip te krijgen op hun emoties.",
    "landing.benefit.qol.title": "Kwaliteit van Leven-test",
    "landing.benefit.qol.blurb": "Reflecteer op 1000 dagen en ontdek je persoonlijke startpunt.",
    "landing.benefit.brain.title": "Breintype-test",
    "landing.benefit.brain.blurb": "Ontdek in een paar minuten welke van de 9 breintypen je bent.",
    "landing.benefit.manual.title": "Breintraining-handleiding",
    "landing.benefit.manual.blurb": "De vier kerntechnieken om grip te krijgen op je gedachten.",
    "landing.benefit.ai.title": "Oefen met een AI-coach",
    "landing.benefit.ai.blurb": "Oefen de Gevoelsomdraaitechniek met onze AI-gids.",
    "landing.benefit.free_pill": "Gratis",
    "landing.cta.register": "Maak je gratis account",
    "landing.cta.sign_in": "Al lid? Log in",
    "landing.sign_in_link": "Inloggen",
    "landing.back_to_landing": "Bekijk wat je gratis krijgt",
    "landing.hero_card_title": "Een rustige plek voor emotionele helderheid en neuro-coaching.",

    // Login form
    "login.welcome_back": "Welkom terug",
    "login.title_part_a": "Log in op",
    "login.title_part_b": "je vertrouwde plek.",
    "login.subtitle": "Zet je reis van zelfontdekking, bewustwording en groei voort.",
    "login.email_label": "E-mail",
    "login.password_label": "Wachtwoord",
    "login.email_placeholder": "jij@voorbeeld.com",
    "login.password_placeholder": "••••••••",
    "login.submit": "Inloggen",
    "login.submitting": "Bezig met inloggen…",
    "login.new_here_prompt": "Nieuw hier?",

    // Footer
    "footer.copyright_prefix": "©",
    "footer.copyright_address": ", Binderij 7-L, 1185 ZH, Amstelveen, Nederland",
    "footer.copyright_org": "Neurosofie Stichting",

    // Register
    "register.headline_a": "Start je",
    "register.headline_b": "reis.",
    "register.lead": "Kies het instituut en de coach waarmee je gaat werken, en stel daarna je inloggegevens in.",
    "register.step.institute": "Instituut",
    "register.step.coach": "Coach",
    "register.step.details": "Gegevens",
    "register.title": "Maak je account aan",
    "register.step_institute": "Kies je instituut",
    "register.step_coach": "Kies je coach",
    "register.step_details": "Jouw gegevens",
    "register.q_institute": "Bij welk instituut hoort jouw coach of trainer?",
    "register.q_coach": "Wie is jouw coach?",
    "register.institute_search_placeholder": "Zoek op naam of locatie…",
    "register.coach_search_placeholder": "Zoek op naam, e-mail of locatie…",
    "register.loading_institutes": "Instituten laden…",
    "register.loading_coaches": "Coaches laden…",
    "register.no_matches": "Geen resultaten.",
    "register.no_coaches_yet": "Dit instituut heeft nog geen coaches. Neem contact met ze op of kies een ander instituut.",
    "register.coach_brain_type": "Breintype {n}",
    "register.search_placeholder": "Zoek op naam, e-mail of locatie",
    "register.location_label": "Locatie",
    "register.brain_type_label": "Breintype",
    "register.back": "Terug",
    "register.continue": "Volgende",
    "register.create_account": "Account aanmaken",
    "register.create_and_start": "Account aanmaken & onboarding starten",
    "register.creating": "Bezig met aanmaken…",
    "register.already_account": "Heb je al een account?",
    "register.sign_in": "Inloggen",
    "register.name_label": "Volledige naam",
    "register.email_label": "E-mail",
    "register.password_label": "Wachtwoord (min. 6 tekens)",
    "register.no_institutes": "Nog geen instituten beschikbaar.",
    "register.no_coaches": "Bij dit instituut zijn nog geen coaches beschikbaar.",
    "register.welcome_toast": "Welkom, {name}!",
    "register.error.load_institutes": "Kon instituten niet laden",
    "register.error.load_coaches": "Kon coaches niet laden",
    "register.error.generic": "Account kon niet worden aangemaakt.",

    // Account
    "account.title": "Mijn Account",
    "account.description": "Werk je inlog-e-mail of wachtwoord bij. Andere profielvelden worden beheerd door je beheerder.",
    "account.profile": "Profiel",
    "account.name": "Naam",
    "account.email": "E-mail",
    "account.role": "Rol",
    "account.change_email.title": "E-mail wijzigen",
    "account.change_email.subtitle": "Werk het e-mailadres bij waarmee je inlogt. Je huidige wachtwoord is nodig.",
    "account.change_email.new": "Nieuw e-mailadres",
    "account.change_email.current_password": "Huidig wachtwoord",
    "account.change_email.submit": "E-mail bijwerken",
    "account.change_email.saving": "Opslaan…",
    "account.change_password.title": "Wachtwoord wijzigen",
    "account.change_password.subtitle": "Gebruik een wachtwoord van minimaal 6 tekens.",
    "account.change_password.current": "Huidig wachtwoord",
    "account.change_password.new": "Nieuw wachtwoord",
    "account.change_password.confirm": "Bevestig nieuw wachtwoord",
    "account.change_password.submit": "Wachtwoord bijwerken",
    "account.calendly.title": "Calendly embed-code",
    "account.calendly.subtitle":
      "Plak hier de volledige embed-snippet van Calendly (of alleen je boekings-URL). Cliënten die om een breintype-controle vragen, boeken dan direct in jouw agenda. Laat leeg om de platformstandaard te gebruiken.",
    "account.calendly.label": "Embed-code of URL",
    "account.calendly.tip_prefix": "Tip: plak een URL die naar een specifieke event verwijst (bijv. ",
    "account.calendly.tip_suffix": ") zodat cliënten direct op de boekingspagina belanden zonder eerst een event te kiezen.",
    "account.calendly.detected": "Gevonden URL",
    "account.calendly.preview": "Popup-voorbeeld",
    "account.calendly.save": "Embed-code opslaan",
    "account.calendly.saving": "Opslaan…",
    "account.calendly.clear": "Embed-code wissen",

    // Overlines
    "overline.coach": "Coach",
    "overline.profile": "Profiel",
    "overline.handleiding": "Handleiding",

    // Coach client list
    "coach_users.title": "Mijn Cliënten",
    "coach_users.description":
      "Beheer je cliënten. Bij hun eerste login worden ze door de 1000-dagen-reflectie geleid.",
    "coach_users.add_client": "Cliënt toevoegen",
    "coach_users.banner.one": "1 cliënt wil graag samenwerken",
    "coach_users.banner.many": "{count} cliënten willen graag samenwerken",
    "coach_users.banner.total_suffix": " · {total} openstaande verzoeken in totaal",
    "coach_users.banner.description":
      "Let op de badges in de lijst hieronder — klik op \"Markeer als afgehandeld\" zodra je contact hebt opgenomen.",
    "coach_users.banner.more": "en nog {n} anderen",
    "coach_users.col.client": "Cliënt",
    "coach_users.col.email": "E-mail",
    "coach_users.col.brain": "Brein",
    "coach_users.col.type": "Type",
    "coach_users.col.onboarding": "Onboarding",
    "coach_users.col.added": "Toegevoegd",
    "coach_users.loading": "Cliënten laden…",
    "coach_users.empty": "Nog geen cliënten. Voeg je eerste toe.",
    "coach_users.onboarded": "Onboarded",
    "coach_users.onboarding_pending": "In afwachting",
    "coach_users.onboarding_complete": "Voltooid",
    "coach_users.mark_handled": "Markeer als afgehandeld",
    "coach_users.dialog.add_title": "Cliënt toevoegen",
    "coach_users.dialog.edit_title": "Cliënt bewerken",
    "coach_users.dialog.add_desc": "Maak het account van de cliënt aan. Bij de eerste login wordt de onboarding doorlopen.",
    "coach_users.dialog.edit_desc": "Werk de gegevens van de cliënt bij. Laat wachtwoord leeg om het huidige te behouden.",
    "coach_users.field.name": "Volledige naam",
    "coach_users.field.email": "E-mail",
    "coach_users.field.initial_password": "Initieel wachtwoord",
    "coach_users.field.new_password_optional": "Nieuw wachtwoord (optioneel)",
    "coach_users.field.brain_type": "Breintype (1–9, optioneel)",
    "coach_users.field.password_placeholder_edit": "Laat leeg om huidige te behouden",
    "coach_users.delete_title": "Deze cliënt verwijderen?",
    "coach_users.delete_description":
      "Dit verwijdert \"{name}\" en hun onboarding-antwoorden definitief. Deze actie kan niet ongedaan worden gemaakt.",

    // Common
    "common.cancel": "Annuleren",
    "common.save": "Opslaan",
    "common.saving": "Opslaan…",
    "common.save_changes": "Wijzigingen opslaan",
    "common.delete": "Verwijderen",
    "common.deleting": "Verwijderen…",
    "common.confirm_delete": "Weet je het zeker?",
    "common.edit": "Bewerken",
    "common.loading": "Laden…",
    "common.send": "Verzenden",
    "common.sending": "Verzenden…",

    // Client types
    "client_type.prospect": "Prospect",
    "client_type.trainee": "Cursist",
    "client_type.coachee": "Coachee",

    // Brain Type page
    "brain_type.title": "Mijn Breintype",
    "brain_type.description_user":
      "Je breintype is een getal van 1 tot 9, toegewezen door je coach. Het is onderdeel van hoe je coaching op jou wordt afgestemd.",
    "brain_type.description_other":
      "Je toegewezen breintype is een getal van 1 tot 9. Kies of wijzig het type dat bij jouw assessment past. Je kunt dit altijd aanpassen.",
    "brain_type.assigned": "Aan jou toegewezen",
    "brain_type.current": "Huidig",
    "brain_type.not_set": "Niet ingesteld",
    "brain_type.coach_assigned": "Door coach toegewezen",
    "brain_type.readonly_note":
      "Alleen je coach of beheerder kan je breintype wijzigen. Ontbreekt het of denk je dat het anders moet zijn, neem dan contact op met je coach.",
    "brain_type.select": "Kies breintype",
    "brain_type.clear": "Selectie wissen",
    "brain_type.save": "Breintype opslaan",
    "brain_type.preliminary_overline": "Voorlopig · uitkomst sneltest",
    "brain_type.notset_overline": "Breintype · nog niet ingesteld",
    "brain_type.preliminary_voorlopig": "Voorlopig type",
    "brain_type.notset_heading": "Je breintype is nog niet ingesteld",
    "brain_type.preliminary_description":
      "Dit voorlopige breintype komt uit de korte Nederlandse zelftest die je tijdens je onboarding hebt gedaan. Die sneltest is slechts ongeveer 2 op de 3 keer betrouwbaar (~66%), omdat het onderliggende model dynamisch is — je antwoorden verschuiven afhankelijk van of je gespannen of ontspannen bent. Je definitieve breintype wordt pas vastgesteld na een korte consult met je coach. Zodra dat is gebeurd, verschijnen de volledige grafiek en het uitgebreide rapport voor jouw type direct op deze pagina.",
    "brain_type.notset_description":
      "Je breintype wordt samen met je coach vastgesteld in een korte consult. Zodra dat is gebeurd, verschijnen de volledige grafiek en het uitgebreide rapport voor jouw type direct op deze pagina.",
    "brain_type.request_consultation": "Vraag een consult bij je coach aan",
    "brain_type.sending_request": "Verzoek verzenden…",
    "brain_type.coach_notified_pending": "Coach geïnformeerd · in afwachting",

    // QoL
    "qol.title": "Kwaliteit van Leven",
    "qol.description":
      "Dagelijkse check-in op vier levensgebieden. Tik op een onderwerp om aan te geven hoe het vandaag gaat.",
    "qol.subject.feel": "Hoe je je voelt",
    "qol.subject.past": "Omgaan met het verleden",
    "qol.subject.future": "Omgaan met de toekomst",
    "qol.subject.inner_self_talk": "Innerlijke dialoog",
    "qol.option.good": "Goed",
    "qol.option.neutral": "Neutraal",
    "qol.option.bad": "Slecht",
    "qol.chart.title": "Kwaliteit van Leven · waarschijnlijkheid over tijd",
    "qol.chart.subtitle": "Dagelijkse projectie vanuit je onboarding-startpunt en de toggles die je hebt ingesteld.",
    "qol.chart.loading": "Laden…",
    "qol.chart.empty": "Stel hieronder je toggles in — de grafiek begint dan te tellen vanaf je accountdatum.",

    // Dagelijkse spreuk + check-in
    "quote.overline": "Spreuk van de dag",
    "quote.source_label": "Bron",
    "quote.loading": "Spreuk van vandaag laden…",
    "checkin.title": "Even inchecken",
    "checkin.subtitle":
      "Voordat we de spreuk van vandaag laten zien — kloppen deze vier instellingen nog met hoe je je nu voelt? Pas aan wat is veranderd, of bevestig dat het nog klopt.",
    "checkin.confirm_button": "Klopt allemaal nog",
    "checkin.saving": "Opslaan…",
    "checkin.close_hint": "Verander wat is veranderd en ga daarna verder.",

    // Dashboard (client home)
    "dashboard.title": "Jouw dashboard",
    "dashboard.description":
      "Een dagelijkse blik op je spreuk, je breintype, je Kwaliteit van Leven-status en je GOT-oefeningen.",
    "dashboard.qol.title": "Kwaliteit van Leven · vandaag",
    "dashboard.qol.updated": "Laatst bijgewerkt",
    "dashboard.qol.unset": "Nog niet ingesteld",
    "dashboard.qol.link": "Open Kwaliteit van Leven",
    "dashboard.qol.empty":
      "Je hebt nog geen Kwaliteit van Leven-instellingen. Open de pagina om te beginnen.",
    "dashboard.bt.title": "Jouw Breintype",
    "dashboard.bt.definitive": "Bevestigd door je coach",
    "dashboard.bt.preliminary": "Voorlopig · uit de sneltest",
    "dashboard.bt.pending_dc": "Dubbelcheck aangevraagd · wacht op coach",
    "dashboard.bt.pending_consult": "Consult aangevraagd · wacht op coach",
    "dashboard.bt.no_test": "Je hebt de breintype-sneltest nog niet gedaan.",
    "dashboard.bt.no_definitive": "Je breintype is nog niet definitief vastgesteld.",
    "dashboard.bt.request_dc": "Vraag een dubbelcheck aan",
    "dashboard.bt.link": "Open je Breintype",
    "dashboard.got.title": "GOT-oefening",
    "dashboard.got.total_one": "sessie gestart",
    "dashboard.got.total_many": "sessies gestart",
    "dashboard.got.completed_one": "afgerond",
    "dashboard.got.completed_many": "afgerond",
    "dashboard.got.link": "Start een nieuwe oefening",
    "dashboard.got.zero_state":
      "Je hebt nog geen GOT geoefend. Probeer eens één korte sessie met de AI-coach.",

    // Request coaching
    "request_coaching.title": "Klaar voor de volgende stap?",
    "request_coaching.idle":
      "Laat je coach weten dat je wilt starten met coaching. Hij of zij krijgt een melding en neemt contact met je op.",
    "request_coaching.pending":
      "Je coach is geïnformeerd. Hij of zij neemt binnenkort contact op. Je kunt zo nodig nog een herinnering sturen.",
    "request_coaching.cta": "Coaching aanvragen",
    "request_coaching.sending": "Verzenden…",
    "request_coaching.send_again": "Stuur nog een herinnering",
    "request_coaching.pending_status": "In afwachting · verzonden {date}",
    "request_coaching.toast.success": "Je coach is geïnformeerd. Hij of zij neemt snel contact op.",

    // Manual chrome
    "manual.overline": "Handleiding",
    "manual.cta.title": "Oefen deze techniek met AI",
    "manual.cta.body":
      "Wil je de Gevoelsomdraaitechniek direct in de praktijk brengen? Onze AI-coach loopt stap voor stap met je mee — precies zoals hierboven beschreven. Je kunt de oefening zo vaak doen als je wilt.",
    "manual.cta.button": "Start GOT-oefening",
    "manual.coach_pitch":
      "Deze oefeningen gaan een stuk makkelijker — en sneller — als je ze samen met een Personal Breintrainer doet. Een coach helpt je de techniek écht eigen te maken en kan met je meedenken in jouw specifieke situatie.",

    // GOT
    "got.overline": "Coaching",
    "got.title": "GOT Oefenen",
    "got.description":
      "Oefen de Gevoelsomdraaitechniek met een AI-coach. Kies of je de cliënt of de coach speelt.",
    "got.role.client": "Speel de cliënt",
    "got.role.coach": "Speel de coach",
    "got.start": "Nieuwe sessie starten",
    "got.starting": "Bezig met starten…",
    "got.placeholder": "Typ je bericht…",
    "got.send": "Verzenden",
    "got.thinking": "Denkt na…",
    "got.session_done_title": "Goed gedaan — nieuwe sessie?",
    "got.session_done_body": "Je hebt deze oefenronde afgerond. Begin opnieuw wanneer je er klaar voor bent.",
    "got.new_session": "Nieuwe sessie",

    // Events
    "events.title": "Aankomende Events",
    "events.description": "Events opgehaald van de website van jouw instituut.",
    "events.loading": "Events laden…",
    "events.empty": "Geen aankomende events gevonden.",
    "events.refresh": "Vernieuwen",
    "events.source": "Bron",

    // Conclusion
    "conclusion.title": "Welkom bij NeuroSophia",
    "conclusion.subtitle":
      "Bedankt voor het invullen van de reflectie. Hier is wat er nu komt.",
    "conclusion.request_coaching_button": "Coaching aanvragen",

    // Errors
    "errors.generic": "Er ging iets mis.",

    // Embed code card (coach + institute admin)
    "embed.coach.title": "Embed-code voor jouw website",
    "embed.coach.subtitle":
      "Plak deze snippet op je eigen website. Prospects die er op klikken komen direct op stap 3 van de aanmelding, met jou al voorgeselecteerd als hun coach. Alleen hun naam, e-mail en wachtwoord hoeven ze nog in te vullen.",
    "embed.institute.title": "Embed-code voor jouw website",
    "embed.institute.subtitle":
      "Plak deze snippet op de website van je instituut. Prospects die er op klikken komen op stap 2 van de aanmelding, waar ze de juiste coach binnen jouw instituut kunnen kiezen. Het instituut is al voorgeselecteerd.",
    "embed.button_label": "Start gratis met NeuroSophia",
    "embed.tab.iframe": "iFrame (in pagina)",
    "embed.tab.button": "Knop (nieuw tabblad)",
    "embed.tab.link": "Directe link",
    "embed.iframe.hint":
      "Plaatst de volledige aanmeldflow in een frame op je pagina. Het beste als je wilt dat bezoekers op je site blijven.",
    "embed.button.hint":
      "Opent NeuroSophia in een nieuw tabblad bij klikken — geen iframe, snelst te plaatsen.",
    "embed.link.hint":
      "De ruwe URL achter bovenstaande snippets. Handig als je hem in je eigen nieuwsbrief-knop wilt verpakken of op social media wilt delen.",
    "embed.copy": "Kopieer",
    "embed.copied": "Gekopieerd!",
    "embed.preview": "Open in nieuw tabblad",
    "embed.copy.toast": "Embed-code gekopieerd naar het klembord.",
    "embed.copy.error": "Kopiëren mislukt — kopieer handmatig.",

    // Onboarding (1000-day reflection)
    "onboarding.welcome": "Welkom, {name}",
    "onboarding.reflection_step": "Reflectie {step} van {total} · 1000 dagen vóór coaching of training",
    "onboarding.final_step": "Laatste stap",
    "onboarding.question_prefix": "Hoe zou je",
    "onboarding.question.emotions.title": "je emoties en gevoelens",
    "onboarding.question.emotions.description": "Kijk terug op de 1000 dagen vóór coaching of training: hoe lieten je emoties en gevoelens zich gelden?",
    "onboarding.question.past.title": "het omgaan met het verleden",
    "onboarding.question.past.description": "Hoe verhield je je tot je verleden — herinneringen, spijt, dankbaarheid?",
    "onboarding.question.future.title": "het omgaan met de toekomst",
    "onboarding.question.future.description": "Hoe verhield je je tot wat komen ging — hoop, angst, plannen?",
    "onboarding.question.inner_self_talk.title": "je innerlijke dialoog",
    "onboarding.question.inner_self_talk.description": "Hoe sprak je innerlijke stem gemiddeld tegen je tijdens die 1000 dagen?",
    "onboarding.question.distribute": "Verdeel precies",
    "onboarding.question.thousand_days": "1000 dagen",
    "onboarding.question.among": "over de zes categorieën hieronder.",
    "onboarding.category.perfect": "Perfecte Dagen",
    "onboarding.category.perfect.hint": "Pure aanwezigheid en vreugde",
    "onboarding.category.very_good": "Zeer Goede Dagen",
    "onboarding.category.very_good.hint": "Licht en verbonden",
    "onboarding.category.good": "Goede Dagen",
    "onboarding.category.good.hint": "Over het algemeen positief",
    "onboarding.category.okayish": "Redelijke Dagen",
    "onboarding.category.okayish.hint": "Neutraal, je redt je",
    "onboarding.category.bad": "Slechte Dagen",
    "onboarding.category.bad.hint": "Zwaar of vastzittend",
    "onboarding.category.very_bad": "Zeer Slechte Dagen",
    "onboarding.category.very_bad.hint": "Diep pijnlijk",
    "onboarding.days_allocated": "Dagen toegekend",
    "onboarding.remaining": "Resterend",
    "onboarding.back": "Terug",
    "onboarding.continue": "Volgende",
    "onboarding.review": "Bekijk",
    "onboarding.review.title_a": "Bekijk je",
    "onboarding.review.title_b": "reflectie.",
    "onboarding.review.body": "Haal even adem. Controleer je antwoorden hieronder — je kunt terug en aanpassen voordat je verstuurt.",
    "onboarding.submit": "Reflectie versturen",
    "onboarding.submitting": "Versturen…",
    "onboarding.toast.must_total": "Elke vraag moet exact 1000 dagen totaliseren.",
    "onboarding.toast.thanks": "Bedankt voor het delen.",

    // Onboarding conclusion
    "conclusion.overline": "Onboarding · conclusie",
    "conclusion.headline_a": "Je startende",
    "conclusion.headline_b": "kwaliteit-van-leven-startpunt",
    "conclusion.headline_c": "is",
    "conclusion.body":
      "We hebben je vier antwoorden (emoties, verleden, toekomst, innerlijke dialoog) gemiddeld met dezelfde formule die je Kwaliteit-van-Leven-grafiek voedt. Waar je op de ABC-NLP-kaart hieronder uitkomt, helpt je bij het kiezen van je volgende stap.",
    "conclusion.subject.feelings": "Gevoelens",
    "conclusion.subject.past": "Het Verleden",
    "conclusion.subject.future": "De Toekomst",
    "conclusion.subject.inner_self_talk": "Innerlijke Dialoog",
    "conclusion.band.coaching.title": "Coaching is je beste volgende stap",
    "conclusion.band.either.title": "Coaching óf training werkt allebei goed",
    "conclusion.band.training.title": "Een trainingsprogramma past het beste bij jou",
    "conclusion.band.coaching.body":
      "Met een gemiddeld startpunt van {avg}% (37% of lager) geeft één-op-één coaching je de gerichte steun om eerst uit de rode zone te komen. Zodra je stabiel boven de 37% zit, kan een trainingsprogramma je verder brengen. Tik hieronder om je coach te vragen te starten met coaching.",
    "conclusion.band.either.body":
      "Met een gemiddeld startpunt van {avg}% (tussen 37% en 50%) zit je in de oranje zone — coaching óf training kan je verder helpen. Vraag hieronder coaching aan als je persoonlijke begeleiding wilt. Een overzicht van relevante trainingsprogramma's verschijnt direct na het afronden van de onboarding.",
    "conclusion.band.training.body":
      "Met een gemiddeld startpunt van {avg}% (boven de 50%) zit je al in de groene zone. Een trainingsprogramma — Practitioner, Master Practitioner of nascholing — is de meest efficiënte volgende stap. Een overzicht van relevante trainingsprogramma's verschijnt direct na het afronden van de onboarding.",
    "conclusion.coach_notified_pending": "Coach geïnformeerd · in afwachting",
    "conclusion.sending": "Verzoek verzenden…",
    "conclusion.request_coaching": "Coaching aanvragen",
    "conclusion.eligible_note": "Coaching aanvragen is alleen beschikbaar voor prospects en cursisten.",
    "conclusion.continue_note": "Je kunt altijd terugkomen bij je coach of de Aankomende Events-pagina openen vanuit het menu.",
    "conclusion.toast.notified": "Je coach is geïnformeerd. Hij of zij neemt binnenkort contact op.",

    // Brain Type Quick Test (onboarding)
    "bt_test.step_label_q1": "Vraag 1 van 2 of 3",
    "bt_test.step_label_q2": "Vraag 2 van 2 of 3",
    "bt_test.step_label_q3": "Vraag 3 van 3",
    "bt_test.title": "Korte breintype check",
    "bt_test.previous": "Vorige vraag",
    "bt_test.result_overline": "Voorlopig resultaat",
    "bt_test.result_title_a": "Je voorlopige breintype is",
    "bt_test.disclaimer_title": "Let op — dit is een sneltest",
    "bt_test.disclaimer_body":
      "Deze test is slechts ongeveer 2 op de 3 keer betrouwbaar (~66%). Het onderliggende model is dynamisch — je antwoorden verschuiven afhankelijk van of je gespannen of ontspannen bent, dus dezelfde persoon kan in verschillende stemmingen op verschillende types uitkomen. Een korte vervolg-consult met je coach is de beste manier om je definitieve breintype te bevestigen.",
    "bt_test.request_double_check": "Vraag een dubbelcheck bij je coach aan",
    "bt_test.requesting": "Verzoek verzenden…",
    "bt_test.requested": "Coach geïnformeerd · onboarding afronden…",
    "bt_test.continue_without": "Doorgaan zonder dubbelcheck",
    "bt_test.saving": "Opslaan…",
    "bt_test.retake_last": "Laatste vraag opnieuw",
    "bt_test.toast.requested": "Je coach is gevraagd om een dubbelcheck.",
    "bt_test.toast.could_not_send": "Verzoek kon niet worden verzonden",
    "bt_test.preview.overline": "Preview · eerste twee alinea's",
    "bt_test.preview.disclaimer":
      "Dit is alleen een voorproefje van je breintype-rapport. De sneltest is nog te onzeker om je nu al de volledige grafiek en de rest van het rapport te tonen. Er is veel meer — maar om dat te ontgrendelen moet je coach je breintype eerst bevestigen met een korte dubbelcheck.",
  },
};
